import { useEffect, useState, useSyncExternalStore } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plane, Loader2, X, RefreshCw, MapPin, ChevronDown, Share2 } from "lucide-react";
import { FlightDetailsPanel, deriveLiveStatus, _normalizeIso, computeDelay } from "./FlightDetailsPanel";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Dashboard-kort som visar bevakade flighter (Swedavia).
 *
 * Klick på en rad → expanderar inline (timeline, WaitTime, teknisk info).
 * Pollar `/flights/watched` var 30s (live ADS-B-data uppdateras varje min på backend).
 */

// AeroDataBox-status (engelsk) → svensk visnings-text. Används när Swedavia
// inte har en uppdaterad status (typiskt för utländska avgångar — Swedavia
// säger "Planerad" tills planet är nästan i Sverige).
const _AERO_STATUS_SV = {
  expected:    "Förväntad",
  checkin:     "Incheckning",
  boarding:    "Boarding",
  finalcall:   "Final call",
  gateclosed:  "Gate stängd",
  departed:    "Avgått",
  enroute:     "I luften",
  diverted:    "Omdirigerad",
  cancelled:   "Inställd",
  canceled:    "Inställd",
  landed:      "Landad",
  arrived:     "Landad",
  delayed:     "Försenad",
  unknown:     "",
};

function _aeroStatusSv(text) {
  if (!text) return "";
  const norm = String(text).toLowerCase().replace(/[\s_-]/g, "");
  return _AERO_STATUS_SV[norm] || text;
}

// --- Extern poll-store (utanför React) så set-state-in-effect-regeln inte triggas ---
let _items = [];
let _loaded = false;
const _listeners = new Set();
let _activeTimer = null;
let _activeInterval = 30_000;
let _activeSubscribers = 0;

async function _fetch() {
  try {
    const { data } = await api.get("/flights/watched/canonical");
    // Canonical endpoint returnerar { items: [{ ...canonical, _legacy: rawWatch }] }
    // Vi använder _legacy som primär watch-objekt (befintliga komponenter
    // läser legacy-shape) men injicerar canonical-skyddade tider tillbaka
    // i last_status så defensive guards garanterat får ren data.
    const all = (data?.items || []).map(_mergeCanonicalIntoLegacy);
    // Filtrera bort watches som tillhör en trip — visas i TripsCard istället
    _items = all.filter((w) => !w.trip_id);
  } catch { /* tyst */ }
  _loaded = true;
  _listeners.forEach((cb) => cb());
}

/**
 * Slå ihop canonical-fält (sanerade tider/status) tillbaka in i legacy-shape
 * så befintliga UI-komponenter kan fortsätta läsa `watch.last_status.*` utan
 * rewrite. Detta är Phase B av canonical-migrationen: alla SK161-typ-buggar
 * (Swedavia poison `actual_time` med landing-tid) är redan filtrerade av
 * backend `to_canonical()` så `_safeDep`-guards i UI:t blir redundanta.
 */
function _mergeCanonicalIntoLegacy(item) {
  const legacy = item._legacy || {};
  const ls = { ...(legacy.last_status || {}) };
  const t = item.times || {};
  const g = item.gates || {};
  const s = item.status || {};
  const a = item.aircraft || {};
  const r = item.route || {};
  const live = item.live || {};

  // Tider — backend har redan filtrerat poisoned actuals via _safe_dep_iso
  // och _actual_makes_sense. Vi använder dessa som SANNING.
  if (t.departure) {
    ls.departure_scheduled = t.departure.scheduled ?? ls.departure_scheduled;
    ls.departure_estimated = t.departure.estimated ?? ls.departure_estimated;
    ls.actual_takeoff_utc  = t.departure.actual ?? ls.actual_takeoff_utc;
  }
  if (t.arrival) {
    ls.arrival_scheduled = t.arrival.scheduled ?? ls.arrival_scheduled;
    ls.arrival_estimated = t.arrival.estimated ?? ls.arrival_estimated;
    ls.actual_landing_utc = t.arrival.actual ?? ls.actual_landing_utc;
  }
  // Status — canonical-koden är källan
  if (s.code) ls.status = s.code;
  if (s.label_swedish) ls.remark_swedish = s.label_swedish;
  if (s.label_aero)    ls.aero_status_text = s.label_aero;

  // Route / aircraft / live: lägg tillbaka för komponenter som vill ha
  // canonical-skyddade fält i stället för rena raw-doc.
  ls.origin            = r.origin?.iata        ?? ls.origin;
  ls.destination       = r.destination?.iata   ?? ls.destination;
  ls.origin_name       = r.origin?.name        ?? ls.origin_name;
  ls.destination_name  = r.destination?.name   ?? ls.destination_name;
  ls.aircraft_model    = a.model               ?? ls.aircraft_model;
  ls.aircraft_registration = a.registration    ?? ls.aircraft_registration;
  ls.airline           = a.airline_name        ?? ls.airline;
  ls.airline_iata      = a.airline_iata        ?? ls.airline_iata;
  ls.icao24            = a.icao24              ?? ls.icao24;
  ls.callsign          = a.callsign            ?? ls.callsign;

  // Live ADS-B-position behåller `live_position`-objektet för on_ground-flag
  // men kompletterar med canonical-fälten för konsumenter som vill ha dem.
  if (live.lat != null)        ls.live_lat = live.lat;
  if (live.lng != null)        ls.live_lng = live.lng;
  if (live.altitude_ft != null) ls.live_altitude_ft = live.altitude_ft;
  if (live.speed_kts != null)   ls.live_speed_kts = live.speed_kts;

  return {
    ...legacy,
    last_status: ls,
    _canonical: item,  // exponera för komponenter som vill läsa canonical direkt
  };
}
function _ensureTimer() {
  if (_activeTimer != null) return;
  void _fetch();
  _activeTimer = setInterval(() => { void _fetch(); }, _activeInterval);
}
function _stopTimer() {
  if (_activeTimer != null) {
    clearInterval(_activeTimer);
    _activeTimer = null;
  }
}
function setPollInterval(ms) {
  if (ms === _activeInterval) return;
  _activeInterval = ms;
  if (_activeSubscribers > 0) {
    _stopTimer();
    _ensureTimer();
  }
}
function subscribeFlights(cb) {
  _listeners.add(cb);
  _activeSubscribers += 1;
  _ensureTimer();
  return () => {
    _listeners.delete(cb);
    _activeSubscribers -= 1;
    if (_activeSubscribers === 0) _stopTimer();
  };
}
const _snapshot = () => _items;
const _serverSnapshot = () => [];
export function refreshFlights() { return _fetch(); }

export function FlightsCard() {
  const { user } = useAuth();
  const items = useSyncExternalStore(subscribeFlights, _snapshot, _serverSnapshot);
  const loading = !_loaded;
  const [refreshing, setRefreshing] = useState({});
  const [sharing, setSharing] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [autoExpanded, setAutoExpanded] = useState(false);

  // Skala poll-intervall baserat på om något är expanderat
  useEffect(() => {
    setPollInterval(expandedId ? 10_000 : 30_000);
  }, [expandedId]);

  // När användaren expanderar en flight: trigga omedelbar backend-poll så
  // OpenSky live-data hämtas direkt (istället för att vänta upp till 15 min
  // på nästa schemalagda poll). Backend deduppar pushar så detta är säkert.
  useEffect(() => {
    if (!expandedId) return;
    let cancelled = false;
    (async () => {
      try {
        await api.post(`/flights/watched/${expandedId}/poll`);
        if (!cancelled) await _fetch();
      } catch { /* tyst — bakgrundspollern fångar upp det */ }
    })();
    return () => { cancelled = true; };
  }, [expandedId]);

  // Auto-expandera första flighten som avgår inom 3h (endast på första laddningen,
  // användarens egna toggle-val respekteras därefter).
  useEffect(() => {
    if (autoExpanded || items.length === 0) return;
    const now = Date.now();
    const threeH = 3 * 60 * 60 * 1000;
    const target = items.find((w) => {
      const iso = w.scheduled_time || w?.last_status?.scheduled_time;
      if (!iso) return false;
      const sched = new Date(iso).getTime();
      if (isNaN(sched)) return false;
      return sched - now < threeH && sched > now - 30 * 60 * 1000;
    });
    // queueMicrotask defererar state-updates utanför effect-fasen,
    // vilket undviker React 19:s set-state-in-effect-regel.
    queueMicrotask(() => {
      if (target) setExpandedId(target.id);
      setAutoExpanded(true);
    });
  }, [items, autoExpanded]);

  const removeWatch = async (id, flightId, isOwner) => {
    const msg = isOwner
      ? `Ta bort bevakningen av ${flightId} för alla familjemedlemmar?`
      : `Sluta se ${flightId} på din Dashboard?`;
    if (!confirm(msg)) return;
    try {
      const { data } = await api.delete(`/flights/watched/${id}`);
      if (data?.action === "unsubscribed") {
        toast.success(`Du följer inte längre ${flightId}`);
      } else {
        toast.success("Borttagen");
      }
      await _fetch();
    } catch {
      toast.error("Kunde inte ta bort");
    }
  };

  const shareWithFamily = async (id, flightId) => {
    setSharing((s) => ({ ...s, [id]: true }));
    try {
      const { data } = await api.post(`/flights/watched/${id}/share-with-family`);
      const newly = data?.newly_added || [];
      if (newly.length === 0) {
        toast.info(`${flightId} delas redan med hela familjen (${data?.total_subscribers || "?"} pers)`);
      } else {
        toast.success(
          `${flightId} delad med ${newly.length} nya: ${newly.join(", ")}`,
        );
      }
      await _fetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte dela");
    } finally {
      setSharing((s) => ({ ...s, [id]: false }));
    }
  };

  const refreshOne = async (id) => {
    setRefreshing((r) => ({ ...r, [id]: true }));
    try {
      await api.post(`/flights/watched/${id}/poll`);
      await _fetch();
    } catch {
      toast.error("Kunde inte uppdatera");
    } finally {
      setRefreshing((r) => ({ ...r, [id]: false }));
    }
  };

  if (loading || items.length === 0) return null;

  return (
    <section data-testid="flights-card" className="px-4">
      <h2 className="text-xs font-bold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2 flex items-center gap-1.5">
        <Plane size={14} />
        Flyg under bevakning
      </h2>
      <ul className="space-y-2">
        {items.map((w) => {
          const isOwner = (w.created_by === user?.id);
          return (
          <li key={w.id}>
            <FlightRow
              watch={w}
              expanded={expandedId === w.id}
              isOwner={isOwner}
              onToggle={() => setExpandedId(expandedId === w.id ? null : w.id)}
              onRefresh={() => refreshOne(w.id)}
              onRemove={() => removeWatch(w.id, w.flight_id, isOwner)}
              onShare={() => shareWithFamily(w.id, w.flight_id)}
              refreshing={!!refreshing[w.id]}
              sharing={!!sharing[w.id]}
            />
            {expandedId === w.id && (
              <FlightDetailsPanel watch={w} />
            )}
          </li>
        );})}
      </ul>
    </section>
  );
}

function FlightRow({ watch, expanded, onToggle, onRemove, onRefresh, onShare,
                     refreshing, sharing, isOwner }) {
  const s = watch.last_status || {};
  const live = deriveLiveStatus(s, watch);
  const statusSv = s.remark_swedish || "";
  const statusEn = s.remark_english || "";
  // AeroDataBox-status filtreras bort när vi har bevis på att planet
  // passerat gate-fasen — annars fastnar UI på "Boarding"/"Final call"
  // i timmar efter att webhook stoppat skicka uppdateringar.
  const aeroNorm = String(s.aero_status_text || "").toLowerCase().replace(/[\s_-]/g, "");
  const _gatePhase = ["checkin", "boarding", "finalcall", "gateclosed"].includes(aeroNorm);
  const _swCodeUpper = String(s.status || "").toUpperCase();
  const _passedGate =
    !!s.actual_takeoff_utc
    || !!s.actual_landing_utc
    || s.taxiing_seen
    || ["enroute", "departed", "landed", "arrived", "diverted"].includes(aeroNorm)
    || ["ACT", "FLT", "LAN", "ARR", "DEP"].includes(_swCodeUpper)
    || ((s.live_position?.altitude_m || 0) >= 300 && !s.live_position?.on_ground);
  const aeroIsInformative = s.aero_status_text
    && aeroNorm !== "expected"
    && aeroNorm !== "unknown"
    && !(_gatePhase && _passedGate);
  const aeroStatusSv = aeroIsInformative ? _aeroStatusSv(s.aero_status_text) : "";
  // Prioritet: live ADS-B → AeroDataBox webhook → Swedavia → Swedavia engelska
  const status = live?.text || aeroStatusSv || statusSv || statusEn;
  const tone = live ? { color: live.color } : statusTone(s);

  // Rad 3 tider: dep_time → arr_time · restid
  // Källprioritet (mest pålitlig först):
  //   ADB-berikade dep/arr_scheduled > Swedavia scheduled_time
  // Anledning: Swedavias FlightInfo-API kan returnera FLERA poster för
  // samma flight_id+date (morgondagens vs ikvällens kvällsflyg som
  // sträcker sig över midnatt). Polling-loopen kan plocka fel instans
  // medan ADB:s `arrival_scheduled`/`departure_scheduled` alltid pekar
  // på exakt det flight ADB berikade vid watch-skapande.
  const isArrival = watch.kind === "arrival";
  // Källprioritet för depIso och arrIso:
  // 1. actual_takeoff_utc / actual_landing_utc — våra mest exakta värden,
  //    härledda från Swedavia + AeroDataBox + ADS-B (wheels-up/touchdown).
  // 2. ADB-berikade departure/arrival_actual — kommer från trafikledning.
  // 3. estimated / scheduled — fallback.
  // OBS: actual_time/estimated_time är PER-INSTANS-fält. För departures sätter
  // Swedavia ofta actual_time = landings-tid (när planet landar på destinationen
  // Tider: efter Phase B (canonical-merge i `_fetch`) är `s.actual_takeoff_utc`
  // och `s.actual_landing_utc` redan filtrerade av backend `_safe_dep_iso` så
  // poisoned-tider (departure_actual == landing-time) är aldrig set här. Vi
  // läser därför rakt utan defensive guards. (Tidigare `_safeDep`-helper var
  // belt-and-suspenders som blev redundant i session 39.)
  const depIso = isArrival
    ? (s.departure_actual || s.departure_estimated || s.departure_scheduled
        || s.actual_time || s.estimated_time || s.scheduled_time)
    : (s.actual_takeoff_utc
        || s.departure_actual
        || s.departure_estimated
        || s.departure_scheduled
        || s.scheduled_time);
  const depSchedIso = isArrival
    ? (s.departure_scheduled || s.scheduled_time)
    : s.scheduled_time;
  const arrIso = isArrival
    ? (s.actual_landing_utc
        || s.arrival_actual || s.arrival_estimated || s.arrival_scheduled
        || s.actual_time || s.estimated_time || s.scheduled_time)
    : (s.actual_landing_utc
        || s.arrival_actual || s.arrival_estimated || s.arrival_scheduled);
  const arrSchedIso = isArrival
    ? (s.arrival_scheduled || s.scheduled_time)
    : s.arrival_scheduled;
  const row3DepTime = formatTime(depIso);
  const row3DepSched = depSchedIso && depIso !== depSchedIso ? formatTime(depSchedIso) : null;
  const row3DepDelay = computeDelay(depSchedIso, depIso);
  const row3ArrTime = formatTime(arrIso);
  // Markör "+1" när ankomst-datum är nästa kalenderdag jämfört med avgång
  // (i avgångsflygplatsens tidszon). Förhindrar förvirring för utländska
  // kvällsavgångar som ankommer efter midnatt lokal Stockholm-tid.
  let row3ArrPlusDay = 0;
  try {
    if (depIso && arrIso) {
      const depTz = isArrival
        ? (s.origin_timezone || "Europe/Stockholm")
        : "Europe/Stockholm";
      const dDep = new Date(_normalizeIso(depIso));
      const dArr = new Date(_normalizeIso(arrIso));
      const depDay = dDep.toLocaleDateString("sv-SE", { timeZone: depTz });
      const arrDay = dArr.toLocaleDateString("sv-SE", { timeZone: depTz });
      if (depDay && arrDay && arrDay > depDay) {
        // Räkna antal dygn (typiskt 1 men kan bli 2 för väldigt långa)
        const oneDay = 24 * 60 * 60 * 1000;
        row3ArrPlusDay = Math.max(
          1,
          Math.round((new Date(arrDay) - new Date(depDay)) / oneDay),
        );
      }
    }
  } catch { /* tyst */ }
  // Visa även den schemalagda ankomsttiden överstruken om beräknad/faktisk
  // tid skiljer sig (klassisk "X istället för Y" på flygplatstavlor).
  const row3ArrSched = arrSchedIso && arrIso !== arrSchedIso ? formatTime(arrSchedIso) : null;
  const row3ArrDelay = computeDelay(arrSchedIso, arrIso);
  const row3Duration = formatTripDuration(depIso, arrIso);

  return (
    <div
      className="card-soft p-3 flex items-start gap-3 cursor-pointer hover:bg-[color:var(--bg-elevated)] transition"
      data-testid={`flight-row-${watch.flight_id}`}
      onClick={(e) => {
        if (e.target.closest("button")) return;
        onToggle();
      }}
    >
      <div
        className="flex flex-col items-center justify-center min-w-[3.25rem] text-center pr-2 border-r border-[color:var(--border-subtle)]"
        style={{ color: tone.color }}
      >
        <Plane
          size={20}
          className={watch.kind === "arrival" ? "rotate-[135deg]" : "-rotate-45"}
          fill={live ? "currentColor" : "none"}
        />
        <span className="text-[10px] font-bold mt-0.5">{watch.flight_id}</span>
      </div>
      <div className="flex-1 min-w-0">
        {watch.airline && (
          <p className="text-sm font-medium leading-snug">
            {watch.airline}
          </p>
        )}
        {/* Avreseflygplats — för avgångs-watches = hem-flygplats (Arlanda),
            för ankomst-watches = utländsk avreseort (t.ex. Palma) */}
        {(() => {
          const isArrival = watch.kind === "arrival";
          const depIata = isArrival
            ? (s.origin || watch.origin || "")
            : (watch.airport_iata || "");
          const depName = isArrival
            ? (s.origin_name || fullAirportName(depIata))
            : fullAirportName(depIata);
          // Datum-badge: formatera AVGÅNGS-ISO i AVGÅNGSFLYGPLATSENS lokala
          // tidszon. Tidigare visades `watch.date` (Swedavias indexerings-
          // datum, som för utländska kvällsflyg listas under lokal
          // ankomstdag) → "20 juni" visades fastän avgång var "19 juni
          // 22:15 Palma-tid". Användaren trodde flighten gick i morgon.
          const depTz = isArrival
            ? (s.origin_timezone || null)
            : "Europe/Stockholm";
          // Tider redan canonical-skyddade — läs rakt utan _safeDepDate-guard
          const depIsoForDate = isArrival
            ? (s.departure_actual || s.departure_estimated || s.departure_scheduled)
            : (s.actual_takeoff_utc
                || s.departure_actual
                || s.departure_estimated
                || s.departure_scheduled
                || s.scheduled_time);
          let depDateLabel = null;
          if (depIsoForDate) {
            try {
              const d = new Date(_normalizeIso(depIsoForDate));
              depDateLabel = d.toLocaleDateString("sv-SE", {
                day: "numeric", month: "short",
                ...(depTz ? { timeZone: depTz } : {}),
              });
            } catch { /* tyst */ }
          }
          // Fallback: Swedavias `watch.date` om vi inte kan räkna från ISO
          const dateLabel = depDateLabel || watch.date;
          if (!depName && !dateLabel) return null;
          return (
            <p className="text-xs leading-snug" data-testid="flight-meta-row">
              {depName && <span>{depName}</span>}
              {depName && dateLabel && <span> · </span>}
              {dateLabel && (
                <span data-testid="flight-date-badge">{dateLabel}</span>
              )}
            </p>
          );
        })()}
        {/* Rad 3: Avgångstid → ankomsttid · restid */}
        <div className="text-xs text-[color:var(--text-primary)] mt-0.5 flex items-center flex-wrap gap-x-1">
          <span className="tabular-nums font-semibold" data-testid={`flight-dep-time-${watch.flight_id}`}>
            {row3DepTime || "—"}
          </span>
          {row3DepDelay && (row3DepDelay.mins > 0 || row3DepDelay.mins < 0) && (
            <span
              className="text-[9px] font-semibold px-1 rounded tabular-nums"
              style={{
                color: row3DepDelay.color,
                backgroundColor: row3DepDelay.mins > 0 ? "#FEE2E2" : "#D1FAE5",
              }}
              title={row3DepSched ? `Sched. ${row3DepSched}` : undefined}
              data-testid={`flight-dep-delay-${watch.flight_id}`}
            >
              {row3DepDelay.label}
            </span>
          )}
          {row3ArrTime && (
            <span className="tabular-nums" data-testid={`flight-arr-time-${watch.flight_id}`}>
              <span className="text-[10px] mx-0.5">→</span>
              {row3ArrTime}
              {row3ArrPlusDay > 0 && (
                <sup
                  className="text-[9px] font-semibold text-[color:var(--moss)] ml-0.5"
                  title={`Ankomst nästa dag (+${row3ArrPlusDay})`}
                  data-testid="flight-arr-plus-day"
                >
                  +{row3ArrPlusDay}
                </sup>
              )}
            </span>
          )}
          {row3ArrDelay && (row3ArrDelay.mins > 0 || row3ArrDelay.mins < 0) && (
            <span
              className="text-[9px] font-semibold px-1 rounded tabular-nums"
              style={{
                color: row3ArrDelay.color,
                backgroundColor: row3ArrDelay.mins > 0 ? "#FEE2E2" : "#D1FAE5",
              }}
              title={row3ArrSched ? `Sched. ${row3ArrSched}` : undefined}
              data-testid={`flight-arr-delay-${watch.flight_id}`}
            >
              {row3ArrDelay.label}
            </span>
          )}
          {row3Duration && (
            <>
              <span className="mx-0.5">·</span>
              <span className="tabular-nums" data-testid="flight-trip-duration">
                {row3Duration}
              </span>
            </>
          )}
        </div>
        {/* Rad 4: Destination/resemål — ankomstort med namn */}
        {(s.destination_name || s.destination || watch.destination_name) && (
          <p className="text-xs text-[color:var(--text-primary)] leading-snug mt-0.5 flex items-center gap-1"
             data-testid={`flight-destination-${watch.flight_id}`}>
            <MapPin size={11} className="shrink-0" />
            <span className="truncate">
              {s.destination_name || s.destination || watch.destination_name}
            </span>
          </p>
        )}
        {/* Tre rader med terminal/gate/incheckning — all viktig info direkt synlig.
            För avgångar (kind=departure) = Swedavia (ARN-sidan, dvs där användaren
            checkar in). För ankomster (kind=arrival, hemresa) = utländska
            avgångsplatsen (origin_*) från AeroDataBox-berikning. Vi faller
            INTE tillbaka på Swedavias `terminal`/`gate` för arrival-watches —
            det är ankomstsidan (ARN) och vilseleder användaren som vill veta
            var hon ska checka in utomlands. Är origin_* tomt → "—" tills ADB
            berikar (ankomstinfo finns i geek-panelen). */}
        <div className="mt-1.5 space-y-0.5 text-[11px] leading-tight" data-testid={`flight-info-stack-${watch.flight_id}`}>
          <InfoLine
            label="Terminal"
            value={watch.kind === "arrival" ? s.origin_terminal : s.terminal}
            testid="flight-terminal-row"
          />
          <InfoLine
            label="Gate"
            value={watch.kind === "arrival" ? s.origin_gate : s.gate}
            testid="flight-gate-row"
          />
          <InfoLine
            label="Incheckning"
            value={watch.kind === "arrival" ? s.origin_checkin : s.checkin_desks}
            testid="flight-checkin-row"
          />
        </div>
        {status && (
          <p
            className="text-[11px] font-semibold mt-1"
            style={{ color: tone.color }}
            data-testid={`flight-status-${watch.flight_id}`}
          >
            {status}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1 items-center">
        <ChevronDown
          size={16}
          className={`text-[color:var(--text-tertiary)] transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
          data-testid={`flight-chevron-${watch.flight_id}`}
        />
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="p-1.5 rounded hover:bg-[color:var(--bg-elevated)] text-[color:var(--text-tertiary)]"
          data-testid={`flight-refresh-${watch.flight_id}`}
          title="Uppdatera nu"
        >
          {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        </button>
        {isOwner && (
          <button
            onClick={onShare}
            disabled={sharing}
            className="p-1.5 rounded hover:bg-[color:var(--bg-elevated)] text-[color:var(--brand-primary)]"
            data-testid={`flight-share-${watch.flight_id}`}
            title="Dela med hela familjen"
          >
            {sharing ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
          </button>
        )}
        <button
          onClick={onRemove}
          className="p-1.5 rounded hover:bg-[color:var(--bg-elevated)] text-[color:var(--danger)]"
          data-testid={`flight-remove-${watch.flight_id}`}
          title={isOwner ? "Ta bort bevakning" : "Sluta se denna flight"}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

function InfoLine({ label, value, testid }) {
  const hasValue = value != null && String(value).trim() !== "";
  return (
    <div
      className="flex items-baseline gap-1.5"
      data-testid={testid}
    >
      <span className="text-[color:var(--text-tertiary)] font-medium min-w-[5.25rem]">
        {label}:
      </span>
      <span
        className={
          hasValue
            ? "font-semibold text-[color:var(--text-primary)] tabular-nums"
            : "italic text-[color:var(--text-tertiary)]"
        }
      >
        {hasValue ? value : "väntar…"}
      </span>
    </div>
  );
}

function fullAirportName(iata) {
  const m = {
    ARN: "Arlanda",
    BMA: "Bromma",
    GOT: "Göteborg Landvetter",
    MMX: "Malmö Sturup",
    LLA: "Luleå",
    UME: "Umeå",
    OSD: "Östersund Åre",
    VBY: "Visby",
    KRN: "Kiruna",
    RNB: "Ronneby",
  };
  return m[(iata || "").toUpperCase()] || iata;
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    // Normalisera AeroDataBox-format "2026-06-19 14:45Z" → "2026-06-19T14:45Z"
    // så Safari (strikt ISO 8601-parser) kan tolka det som UTC korrekt.
    const normalized = typeof iso === "string"
      ? iso.replace(" ", "T")
      : iso;
    return new Date(normalized).toLocaleTimeString("sv-SE", {
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
}

function formatTripDuration(depIso, arrIso) {
  // Returnerar "3,45h" eller "45min" om <1h
  if (!depIso || !arrIso) return "";
  try {
    const dep = typeof depIso === "string" ? depIso.replace(" ", "T") : depIso;
    const arr = typeof arrIso === "string" ? arrIso.replace(" ", "T") : arrIso;
    const ms = new Date(arr).getTime() - new Date(dep).getTime();
    if (!isFinite(ms) || ms <= 0) return "";
    const totalMin = Math.round(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h <= 0) return `${m}min`;
    return `${h},${String(m).padStart(2, "0")}h`;
  } catch { return ""; }
}

function statusTone(status) {
  // status kan vara objekt (med gate_action) eller string-kod
  if (typeof status === "object" && status) {
    const ga = (status.gate_action || "").toUpperCase();
    if (ga === "C") return { color: "#DC2626" };           // Gate stängd
    if (["B", "G", "F"].includes(ga)) return { color: "#7C3AED" }; // Boarding/Gate
    status = status.status;
  }
  const c = (status || "").toUpperCase();
  if (["CNL", "CAN", "DEL"].includes(c)) return { color: "#DC2626" };
  if (["SEQ", "DLY", "EXP", "NXT"].includes(c)) return { color: "#D97706" };
  if (["ACT", "DEP", "GOD", "FLY"].includes(c)) return { color: "#059669" };
  if (["LAN", "ARR"].includes(c)) return { color: "#059669" };
  return { color: "var(--text-primary)" };
}
